import { format, formatDistanceToNowStrict } from 'date-fns';
import { enGB, ro } from 'date-fns/locale';
import type { Locale } from 'date-fns';
import { useTranslation } from 'react-i18next';

/**
 * date-fns locales, keyed by the language codes the app ships (spec §8.9).
 *
 * `enGB` rather than `enUS`: this is a European product and "22 August 2026"
 * is what its readers expect to see.
 */
const LOCALES: Record<string, Locale> = { en: enGB, ro };

function localeFor(language: string): Locale {
  // "ro-RO" and "ro" are the same locale as far as date-fns is concerned.
  return LOCALES[language.split('-')[0] ?? 'en'] ?? enGB;
}

/**
 * "3 hours", in the reader's language.
 *
 * Returned as a function rather than a component so the same call site can put
 * it in a `<time>` element, an aria-label or a tooltip.
 */
export function useRelativeTime(): (value: string | Date, withSuffix?: boolean) => string {
  const { i18n } = useTranslation();
  const locale = localeFor(i18n.language);

  return (value, withSuffix = false) =>
    formatDistanceToNowStrict(new Date(value), { addSuffix: withSuffix, locale });
}

/** A written-out date: "22 August 2026" / "22 august 2026". */
export function useFormatDate(): (value: string | Date, pattern?: string) => string {
  const { i18n } = useTranslation();
  const locale = localeFor(i18n.language);

  return (value, pattern = 'd MMMM yyyy') => format(new Date(value), pattern, { locale });
}
